import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { API_CONFIG, ApiCallingV2Service, ApiResponseOptions } from '@zayan-erp/shared';
import { catchError, map, Observable, throwError } from 'rxjs';
import { Workflow } from '@zayan-erp/administration';

/**
 * The configuration screen owns the save spinner and outcome toast, so its requests take no
 * full-screen loader and no automatic toast from the shared HTTP layer.
 */
const REPORTED_BY_CALLER: ApiResponseOptions = { showLoader: false, showMessage: false, showError: false };

@Injectable({
  providedIn: 'root'
})
export class WorkflowService {
  readonly #http = inject(ApiCallingV2Service);
  readonly #apiConfig = inject(API_CONFIG);

  readonly #baseUrl = `${this.#apiConfig.baseUrlV2}/workflow`;

  getAllWorkflows(): Observable<Workflow[]> {
    return this.#http
      .getData<Workflow[]>(this.#baseUrl, REPORTED_BY_CALLER)
      .pipe(map((workflows: Workflow[] | null) => workflows ?? []));
  }

  getworkflowById(id: string): Observable<Workflow> {
    return this.#http
      .getData<Workflow>(`${this.#baseUrl}/${id}`, { showLoader: false, showMessage: false })
      .pipe(map((workflow: Workflow | null) => workflow ?? ({} as Workflow)));
  }

  createWorkflow(workflow: Workflow): Observable<Workflow> {
    return this.#http.postData<Workflow>(this.#baseUrl, { message: 'Workflow created successfully' }, workflow);
  }

  /**
   * Autosaved from the configuration screen, which shows its own spinner and toast. The rejection is
   * narrowed to the API's own message so the caller can toast it without reaching into the HTTP
   * layer, and falls back to an empty message when the server sends none.
   */
  updateWorkflow(workflow: Workflow): Observable<Workflow> {
    return this.#http
      .putData<Workflow>(`${this.#baseUrl}/${workflow.id}`, workflow, REPORTED_BY_CALLER)
      .pipe(catchError((error: HttpErrorResponse) => throwError(() => new Error(error.error?.message ?? ''))));
  }

  deleteWorkflow(workflowId: string): Observable<void> {
    return this.#http.deleteData<void>(`${this.#baseUrl}/${workflowId}`, { message: 'Workflow deleted successfully' });
  }

  toggleWorkflowStatus(workflowId: string, status: boolean): Observable<void> {
    return this.#http.putData<void>(
      `${this.#baseUrl}/${workflowId}/toggle`,
      { status },
      { message: 'Workflow status updated successfully' }
    );
  }
}
